/**
 * Client Report Page
 * ==================
 * Client-facing monthly report with operations, financial, and compliance data.
 * Includes PDF download, email functionality, delivery history, and auto-report settings.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { 
  FileText, DollarSign, CheckCircle2, AlertTriangle, 
  Download, Mail, Building2, Users, TrendingUp, Calendar,
  ArrowUp, ArrowDown, X, Send, Clock, Loader2, ChevronRight,
  Settings, RefreshCw, Play
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Period options
const PERIODS = [
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'quarter', label: 'Last 3 Months' },
  { value: 'rolling_30', label: 'Last 30 Days' },
];

// Tier colors
const TIER_COLORS = {
  'A+': 'bg-emerald-100 text-emerald-800',
  'A': 'bg-green-100 text-green-800',
  'B': 'bg-blue-100 text-blue-800',
  'C': 'bg-yellow-100 text-yellow-800',
  'D': 'bg-red-100 text-red-800',
};

export default function ClientReportPage() {
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('last_month');
  const [report, setReport] = useState(null);
  const [clientId, setClientId] = useState(null);
  
  // Email panel state
  const [showEmailPanel, setShowEmailPanel] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [emailForm, setEmailForm] = useState({
    recipients: '',
    cc: '',
    bcc: '',
    subject: '',
    message: ''
  });
  
  // PDF download state
  const [pdfDownloading, setPdfDownloading] = useState(false);
  
  // Delivery history state
  const [showHistory, setShowHistory] = useState(false);
  const [deliveryHistory, setDeliveryHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  
  // Auto-report settings state
  const [showAutoSettings, setShowAutoSettings] = useState(false);
  const [autoSettings, setAutoSettings] = useState(null);
  const [autoSettingsLoading, setAutoSettingsLoading] = useState(false);
  const [autoSettingsSaving, setAutoSettingsSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);

  // Get client ID from URL or user context
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get('client_id');
    if (id) setClientId(id);
  }, []);

  // Fetch report data
  const fetchReport = useCallback(async () => {
    // Don't fetch if we have a client_id param but clientId state not yet set
    const urlParams = new URLSearchParams(window.location.search);
    const urlClientId = urlParams.get('client_id');
    if (urlClientId && !clientId) {
      return; // Wait for clientId to be set from URL params
    }
    
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const url = clientId 
        ? `${API}/api/clients/${clientId}/report?period=${period}`
        : `${API}/api/client/report?period=${period}`;

      const res = await axios.get(url, { headers });

      if (res.data.ok) {
        setReport(res.data.report);
      }
    } catch (err) {
      console.error('Error fetching report:', err);
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [period, clientId]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  // PDF Download handler
  const handleExportPDF = async () => {
    if (!clientId && !report?.client_id) {
      toast.error('Client ID required');
      return;
    }
    
    setPdfDownloading(true);
    try {
      const token = localStorage.getItem('token');
      const targetClientId = clientId || report?.client_id;
      
      // Build URL with query params
      const params = new URLSearchParams({
        client_id: targetClientId,
        period: period
      });
      
      // Fetch PDF as blob
      const response = await axios.get(`${API}/api/client/report/pdf?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from headers or generate one
      const contentDisposition = response.headers['content-disposition'];
      let filename = `client-report-${period}.pdf`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match) filename = match[1];
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded successfully');
    } catch (err) {
      console.error('PDF download error:', err);
      toast.error(err.response?.data?.detail || 'Failed to download PDF');
    } finally {
      setPdfDownloading(false);
    }
  };

  // Email report handler
  const handleEmailReport = async (e) => {
    e.preventDefault();
    
    const recipients = emailForm.recipients.split(',').map(s => s.trim()).filter(Boolean);
    if (recipients.length === 0) {
      toast.error('At least one recipient email is required');
      return;
    }
    
    const targetClientId = clientId || report?.client_id;
    if (!targetClientId) {
      toast.error('Client ID required');
      return;
    }
    
    setEmailSending(true);
    try {
      const token = localStorage.getItem('token');
      
      const payload = {
        client_id: targetClientId,
        period: period,
        recipients: recipients,
        cc: emailForm.cc ? emailForm.cc.split(',').map(s => s.trim()).filter(Boolean) : [],
        bcc: emailForm.bcc ? emailForm.bcc.split(',').map(s => s.trim()).filter(Boolean) : [],
        subject: emailForm.subject || undefined,
        message: emailForm.message || undefined
      };
      
      const response = await axios.post(`${API}/api/client/report/email`, payload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data.ok) {
        toast.success(`Report emailed to ${recipients.length} recipient(s)`);
        setShowEmailPanel(false);
        setEmailForm({ recipients: '', cc: '', bcc: '', subject: '', message: '' });
        
        // Refresh delivery history if showing
        if (showHistory) {
          fetchDeliveryHistory();
        }
      } else {
        toast.error(response.data.error || 'Failed to send email');
      }
    } catch (err) {
      console.error('Email send error:', err);
      toast.error(err.response?.data?.error || err.response?.data?.detail || 'Failed to send email');
    } finally {
      setEmailSending(false);
    }
  };
  
  // Fetch delivery history
  const fetchDeliveryHistory = async () => {
    setHistoryLoading(true);
    try {
      const token = localStorage.getItem('token');
      const targetClientId = clientId || report?.client_id;
      
      const params = targetClientId ? `?client_id=${targetClientId}` : '';
      const response = await axios.get(`${API}/api/client/report/delivery-history${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.ok) {
        setDeliveryHistory(response.data.logs || []);
      }
    } catch (err) {
      console.error('History fetch error:', err);
    } finally {
      setHistoryLoading(false);
    }
  };
  
  // Toggle history panel
  const toggleHistory = () => {
    if (!showHistory) {
      fetchDeliveryHistory();
    }
    setShowHistory(!showHistory);
  };
  
  // Fetch auto-report settings
  const fetchAutoSettings = async () => {
    if (!clientId) return;
    
    setAutoSettingsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/client/report/settings?client_id=${clientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.ok) {
        setAutoSettings(response.data.settings);
      }
    } catch (err) {
      console.error('Failed to fetch auto settings:', err);
    } finally {
      setAutoSettingsLoading(false);
    }
  };
  
  // Toggle auto-settings panel
  const toggleAutoSettings = () => {
    if (!showAutoSettings && !autoSettings) {
      fetchAutoSettings();
    }
    setShowAutoSettings(!showAutoSettings);
  };
  
  // Save auto-report settings
  const saveAutoSettings = async () => {
    if (!autoSettings || !clientId) return;
    
    setAutoSettingsSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      const payload = {
        ...autoSettings,
        client_id: clientId
      };
      
      const response = await axios.post(`${API}/api/client/report/settings`, payload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data.ok) {
        setAutoSettings(response.data.settings);
        toast.success('Auto-report settings saved');
        
        if (response.data.invalid_recipients?.length > 0) {
          toast.warning(`Invalid emails removed: ${response.data.invalid_recipients.join(', ')}`);
        }
      } else {
        toast.error(response.data.error || 'Failed to save settings');
      }
    } catch (err) {
      console.error('Save settings error:', err);
      toast.error(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setAutoSettingsSaving(false);
    }
  };
  
  // Send test report
  const sendTestReport = async () => {
    if (!clientId || !autoSettings?.recipients?.length) {
      toast.error('Please add at least one recipient before sending test');
      return;
    }
    
    setSendingTest(true);
    try {
      const token = localStorage.getItem('token');
      
      const response = await axios.post(`${API}/api/client/report/send-test`, {
        client_id: clientId,
        to: autoSettings.recipients,
        cc: autoSettings.cc || [],
        bcc: autoSettings.bcc || []
      }, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data.ok) {
        toast.success('Test report sent successfully');
        fetchDeliveryHistory();
      } else {
        toast.error(response.data.error || 'Failed to send test report');
      }
    } catch (err) {
      console.error('Send test error:', err);
      toast.error(err.response?.data?.detail || 'Failed to send test report');
    } finally {
      setSendingTest(false);
    }
  };
  
  // Update auto settings field
  const updateAutoSetting = (field, value) => {
    setAutoSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-teal-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="p-8 text-center">
          <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
          <p className="text-slate-600">No report data available</p>
        </Card>
      </div>
    );
  }

  const summary = report.summary || {};
  const financial = report.financial || {};
  const compliance = report.compliance || {};
  const contractors = report.contractors || [];
  const sites = report.sites || [];

  // Format currency
  const formatCurrency = (val) => `£${(val || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}`;

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="client-report-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 print:border-none">
        <div className="max-w-6xl mx-auto px-6 py-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">{report.client_name}</h1>
              <p className="text-slate-500">
                Monthly Report: {formatDate(report.period?.start)} - {formatDate(report.period?.end)}
              </p>
            </div>
            <div className="flex items-center gap-3 print:hidden">
              {/* Period Selector */}
              <select
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                className="px-4 py-2 rounded-xl border border-slate-200 bg-white"
                data-testid="period-selector"
              >
                {PERIODS.map(p => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
              
              {/* Export Buttons */}
              <Button 
                onClick={handleExportPDF} 
                variant="outline" 
                className="gap-2"
                disabled={pdfDownloading}
                data-testid="download-pdf-btn"
              >
                {pdfDownloading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                PDF
              </Button>
              <Button 
                onClick={() => setShowEmailPanel(!showEmailPanel)} 
                variant={showEmailPanel ? "default" : "outline"}
                className="gap-2"
                data-testid="email-report-btn"
              >
                <Mail className="h-4 w-4" />
                Email
              </Button>
              <Button
                onClick={toggleHistory}
                variant="ghost"
                size="icon"
                className="h-10 w-10"
                data-testid="delivery-history-btn"
                title="Delivery History"
              >
                <Clock className="h-4 w-4" />
              </Button>
              <Button
                onClick={toggleAutoSettings}
                variant={showAutoSettings ? "default" : "ghost"}
                size="icon"
                className="h-10 w-10"
                data-testid="auto-settings-btn"
                title="Auto-Report Settings"
              >
                <Settings className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8 space-y-8">
        
        {/* Email Panel - Inline sliding panel */}
        {showEmailPanel && (
          <Card className="bg-white border-teal-200 shadow-lg" data-testid="email-panel">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-teal-800">
                  <Mail className="h-5 w-5" />
                  Email Report
                </CardTitle>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={() => setShowEmailPanel(false)}
                  className="h-8 w-8"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-sm text-slate-500">
                Send this report as a PDF attachment to recipients
              </p>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleEmailReport} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Recipients <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="text"
                      placeholder="email1@example.com, email2@example.com"
                      value={emailForm.recipients}
                      onChange={(e) => setEmailForm({...emailForm, recipients: e.target.value})}
                      className="w-full"
                      data-testid="email-recipients-input"
                      required
                    />
                    <p className="text-xs text-slate-500 mt-1">Separate multiple emails with commas</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">CC</label>
                    <Input
                      type="text"
                      placeholder="cc@example.com"
                      value={emailForm.cc}
                      onChange={(e) => setEmailForm({...emailForm, cc: e.target.value})}
                      className="w-full"
                      data-testid="email-cc-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">BCC</label>
                    <Input
                      type="text"
                      placeholder="bcc@example.com"
                      value={emailForm.bcc}
                      onChange={(e) => setEmailForm({...emailForm, bcc: e.target.value})}
                      className="w-full"
                      data-testid="email-bcc-input"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Subject (optional)</label>
                  <Input
                    type="text"
                    placeholder={`Monthly Service Report – ${report?.client_name || 'Client'}`}
                    value={emailForm.subject}
                    onChange={(e) => setEmailForm({...emailForm, subject: e.target.value})}
                    className="w-full"
                    data-testid="email-subject-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Message (optional)</label>
                  <textarea
                    placeholder="Add a custom message to accompany the report..."
                    value={emailForm.message}
                    onChange={(e) => setEmailForm({...emailForm, message: e.target.value})}
                    rows={3}
                    className="w-full px-3 py-2 rounded-xl border border-slate-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
                    data-testid="email-message-input"
                  />
                </div>
                
                <div className="flex items-center gap-3 pt-2">
                  <Button 
                    type="submit" 
                    disabled={emailSending}
                    className="bg-teal-600 hover:bg-teal-700 gap-2"
                    data-testid="send-email-btn"
                  >
                    {emailSending ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4" />
                        Send Report
                      </>
                    )}
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline"
                    onClick={() => setShowEmailPanel(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}
        
        {/* Delivery History Panel */}
        {showHistory && (
          <Card className="bg-slate-50 border-slate-200" data-testid="delivery-history-panel">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-slate-800">
                  <Clock className="h-5 w-5" />
                  Delivery History
                </CardTitle>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={() => setShowHistory(false)}
                  className="h-8 w-8"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {historyLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                </div>
              ) : deliveryHistory.length > 0 ? (
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {deliveryHistory.map((log, idx) => (
                    <div 
                      key={log.id || idx} 
                      className="flex items-start gap-3 p-3 bg-white rounded-xl border border-slate-100"
                    >
                      <div className={`w-2 h-2 rounded-full mt-2 ${log.status === 'sent' ? 'bg-green-500' : 'bg-red-500'}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium text-slate-800">
                            {log.recipients?.join(', ') || 'Unknown'}
                          </span>
                          <Badge variant={log.status === 'sent' ? 'default' : 'destructive'} className="text-xs">
                            {log.status}
                          </Badge>
                        </div>
                        <p className="text-xs text-slate-500 mt-1">
                          {formatDate(log.sent_at)} • Sent by {log.sent_by_name || 'System'}
                        </p>
                        {log.error && (
                          <p className="text-xs text-red-600 mt-1">{log.error}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500 text-center py-6">No delivery history found</p>
              )}
            </CardContent>
          </Card>
        )}
        
        {/* Auto-Report Settings Panel */}
        {showAutoSettings && (
          <Card className="bg-white border-indigo-200 shadow-lg" data-testid="auto-settings-panel">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-indigo-800">
                  <Settings className="h-5 w-5" />
                  Auto-Report Settings
                </CardTitle>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={() => setShowAutoSettings(false)}
                  className="h-8 w-8"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-sm text-slate-500">
                Configure automatic monthly report delivery for this client
              </p>
            </CardHeader>
            <CardContent>
              {autoSettingsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                </div>
              ) : autoSettings ? (
                <div className="space-y-6">
                  {/* Enable Toggle */}
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                    <div>
                      <p className="font-medium text-slate-800">Enable Auto-Send</p>
                      <p className="text-sm text-slate-500">Automatically send reports on the configured day each month</p>
                    </div>
                    <Switch
                      checked={autoSettings.enabled || false}
                      onCheckedChange={(checked) => updateAutoSetting('enabled', checked)}
                      data-testid="auto-enabled-toggle"
                    />
                  </div>
                  
                  {/* Schedule */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Frequency</label>
                      <select
                        value={autoSettings.frequency || 'monthly'}
                        onChange={(e) => updateAutoSetting('frequency', e.target.value)}
                        className="w-full px-3 py-2 rounded-xl border border-slate-200"
                        data-testid="auto-frequency-select"
                      >
                        <option value="monthly">Monthly</option>
                        <option value="weekly" disabled>Weekly (Coming Soon)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Send Day of Month</label>
                      <Input
                        type="number"
                        min="1"
                        max="28"
                        value={autoSettings.send_day_of_month || 1}
                        onChange={(e) => updateAutoSetting('send_day_of_month', parseInt(e.target.value) || 1)}
                        className="w-full"
                        data-testid="auto-send-day-input"
                      />
                    </div>
                  </div>
                  
                  {/* Recipients */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Recipients <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="text"
                      placeholder="email1@example.com, email2@example.com"
                      value={autoSettings.recipients?.join(', ') || ''}
                      onChange={(e) => updateAutoSetting('recipients', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                      className="w-full"
                      data-testid="auto-recipients-input"
                    />
                    <p className="text-xs text-slate-500 mt-1">Separate multiple emails with commas</p>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">CC</label>
                      <Input
                        type="text"
                        placeholder="cc@example.com"
                        value={autoSettings.cc?.join(', ') || ''}
                        onChange={(e) => updateAutoSetting('cc', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                        className="w-full"
                        data-testid="auto-cc-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">BCC</label>
                      <Input
                        type="text"
                        placeholder="bcc@example.com"
                        value={autoSettings.bcc?.join(', ') || ''}
                        onChange={(e) => updateAutoSetting('bcc', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                        className="w-full"
                        data-testid="auto-bcc-input"
                      />
                    </div>
                  </div>
                  
                  {/* Include Options */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-3">Include in Report</label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {[
                        { key: 'include_financials', label: 'Financials' },
                        { key: 'include_compliance', label: 'Compliance' },
                        { key: 'include_contractors', label: 'Contractors' },
                        { key: 'include_sites', label: 'Sites' },
                        { key: 'include_commentary', label: 'Commentary' },
                      ].map(opt => (
                        <label key={opt.key} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg cursor-pointer">
                          <input
                            type="checkbox"
                            checked={autoSettings[opt.key] !== false}
                            onChange={(e) => updateAutoSetting(opt.key, e.target.checked)}
                            className="rounded border-slate-300"
                          />
                          <span className="text-sm text-slate-700">{opt.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  {/* Custom Messages */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Custom Intro Message</label>
                      <textarea
                        placeholder="Optional custom introduction..."
                        value={autoSettings.custom_intro_message || ''}
                        onChange={(e) => updateAutoSetting('custom_intro_message', e.target.value)}
                        rows={2}
                        className="w-full px-3 py-2 rounded-xl border border-slate-200 resize-none"
                        data-testid="auto-intro-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Custom Signoff</label>
                      <textarea
                        placeholder="Optional custom signoff..."
                        value={autoSettings.custom_signoff || ''}
                        onChange={(e) => updateAutoSetting('custom_signoff', e.target.value)}
                        rows={2}
                        className="w-full px-3 py-2 rounded-xl border border-slate-200 resize-none"
                        data-testid="auto-signoff-input"
                      />
                    </div>
                  </div>
                  
                  {/* Status Info */}
                  {(autoSettings.last_sent_at || autoSettings.next_run_at) && (
                    <div className="grid md:grid-cols-2 gap-4 p-4 bg-slate-50 rounded-xl">
                      {autoSettings.last_sent_at && (
                        <div>
                          <p className="text-xs text-slate-500">Last Sent</p>
                          <p className="text-sm font-medium text-slate-700">{formatDate(autoSettings.last_sent_at)}</p>
                          {autoSettings.last_sent_period_key && (
                            <p className="text-xs text-slate-400">Period: {autoSettings.last_sent_period_key}</p>
                          )}
                        </div>
                      )}
                      {autoSettings.next_run_at && autoSettings.enabled && (
                        <div>
                          <p className="text-xs text-slate-500">Next Scheduled</p>
                          <p className="text-sm font-medium text-slate-700">{formatDate(autoSettings.next_run_at)}</p>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Action Buttons */}
                  <div className="flex items-center gap-3 pt-2">
                    <Button 
                      onClick={saveAutoSettings}
                      disabled={autoSettingsSaving}
                      className="bg-indigo-600 hover:bg-indigo-700 gap-2"
                      data-testid="save-auto-settings-btn"
                    >
                      {autoSettingsSaving ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <CheckCircle2 className="h-4 w-4" />
                          Save Settings
                        </>
                      )}
                    </Button>
                    <Button 
                      onClick={sendTestReport}
                      disabled={sendingTest || !autoSettings.recipients?.length}
                      variant="outline"
                      className="gap-2"
                      data-testid="send-test-report-btn"
                    >
                      {sendingTest ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Play className="h-4 w-4" />
                          Send Test Report
                        </>
                      )}
                    </Button>
                    <Button 
                      onClick={fetchAutoSettings}
                      variant="ghost"
                      size="icon"
                      className="h-10 w-10"
                      title="Refresh Settings"
                    >
                      <RefreshCw className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-slate-500 text-center py-6">Failed to load settings</p>
              )}
            </CardContent>
          </Card>
        )}
        
        {/* KPI Summary Cards */}
        <section>
          <h2 className="text-lg font-semibold text-slate-800 mb-4">Summary</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KPICard 
              label="Completed Jobs" 
              value={summary.completed_jobs || 0}
              icon={CheckCircle2}
              color="green"
            />
            <KPICard 
              label="Open Jobs" 
              value={summary.open_jobs || 0}
              icon={FileText}
              color="blue"
            />
            <KPICard 
              label="Overdue Jobs" 
              value={summary.overdue_jobs || 0}
              icon={AlertTriangle}
              color="red"
              alert={summary.overdue_jobs > 0}
            />
            <KPICard 
              label="Total Spend" 
              value={formatCurrency(financial.paid_value)}
              icon={DollarSign}
              color="emerald"
              isLarge
            />
          </div>
        </section>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-3 gap-8">
          
          {/* Left Column - Financial & Compliance */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Financial Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-emerald-600" />
                  Financial Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-2 font-medium text-slate-600">Category</th>
                        <th className="text-right py-3 px-2 font-medium text-slate-600">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-slate-100">
                        <td className="py-3 px-2 text-slate-700">Approved Quote Value</td>
                        <td className="py-3 px-2 text-right font-medium">{formatCurrency(financial.approved_value)}</td>
                      </tr>
                      <tr className="border-b border-slate-100">
                        <td className="py-3 px-2 text-slate-700">Invoiced Value</td>
                        <td className="py-3 px-2 text-right font-medium">{formatCurrency(financial.invoiced_value)}</td>
                      </tr>
                      <tr className="border-b border-slate-100">
                        <td className="py-3 px-2 text-slate-700">Paid Value</td>
                        <td className="py-3 px-2 text-right font-medium text-emerald-600">{formatCurrency(financial.paid_value)}</td>
                      </tr>
                      <tr className="bg-slate-50">
                        <td className="py-3 px-2 font-semibold text-slate-800">Outstanding</td>
                        <td className="py-3 px-2 text-right font-bold text-amber-600">{formatCurrency(financial.outstanding_value)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="flex gap-4 mt-4 text-sm">
                  <span className="text-slate-500">Quotes Pending: <strong>{financial.quotes_pending || 0}</strong></span>
                  <span className="text-slate-500">Invoices Pending: <strong>{financial.invoices_pending || 0}</strong></span>
                </div>
              </CardContent>
            </Card>

            {/* Compliance Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-blue-600" />
                  Compliance Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6">
                  {/* PPM Status */}
                  <div>
                    <h4 className="text-sm font-medium text-slate-600 mb-3">PPM Status</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-slate-600">Total PPM Jobs</span>
                        <span className="font-medium">{compliance.ppm_total || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-slate-600">Completed</span>
                        <span className="font-medium text-green-600">{compliance.ppm_completed || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-slate-600">Due</span>
                        <span className="font-medium">{compliance.ppm_due || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-slate-600">Overdue</span>
                        <span className={`font-medium ${compliance.ppm_overdue > 0 ? 'text-red-600' : ''}`}>
                          {compliance.ppm_overdue || 0}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Compliance Cases */}
                  <div>
                    <h4 className="text-sm font-medium text-slate-600 mb-3">Compliance Cases</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-slate-600">Total Cases</span>
                        <span className="font-medium">{compliance.compliance_cases_total || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-slate-600">Open</span>
                        <span className={`font-medium ${compliance.compliance_cases_open > 0 ? 'text-amber-600' : ''}`}>
                          {compliance.compliance_cases_open || 0}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-slate-600">Compliance Rate</span>
                        <span className={`font-medium ${compliance.compliance_rate >= 90 ? 'text-green-600' : 'text-amber-600'}`}>
                          {compliance.compliance_rate || 100}%
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Site Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-indigo-600" />
                  Site Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-2 font-medium text-slate-600">Site</th>
                        <th className="text-center py-3 px-2 font-medium text-slate-600">Total</th>
                        <th className="text-center py-3 px-2 font-medium text-slate-600">Completed</th>
                        <th className="text-center py-3 px-2 font-medium text-slate-600">Open</th>
                        <th className="text-center py-3 px-2 font-medium text-slate-600">Overdue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sites.length > 0 ? sites.slice(0, 10).map((site, idx) => (
                        <tr key={site.site_id || idx} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-3 px-2 font-medium text-slate-800">{site.site_name}</td>
                          <td className="py-3 px-2 text-center">{site.total_jobs}</td>
                          <td className="py-3 px-2 text-center text-green-600">{site.completed}</td>
                          <td className="py-3 px-2 text-center">{site.open}</td>
                          <td className="py-3 px-2 text-center">
                            <span className={site.overdue > 0 ? 'text-red-600 font-medium' : ''}>
                              {site.overdue}
                            </span>
                          </td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan={5} className="py-6 text-center text-slate-500">No site data</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                {sites.length > 10 && (
                  <p className="text-xs text-slate-500 mt-3 text-center">
                    Showing top 10 of {sites.length} sites
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Contractors */}
          <div className="space-y-6">
            
            {/* Contractor Performance */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-teal-600" />
                  Top Contractors
                </CardTitle>
              </CardHeader>
              <CardContent>
                {contractors.length > 0 ? (
                  <div className="space-y-4">
                    {contractors.map((c, idx) => (
                      <div 
                        key={c.id || idx} 
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-xl"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-800 truncate">{c.name || 'Unknown'}</p>
                          <p className="text-xs text-slate-500">
                            {c.completed} of {c.jobs} jobs completed
                          </p>
                        </div>
                        <div className="flex items-center gap-2 ml-3">
                          {c.score && (
                            <span className={`text-sm font-bold ${
                              c.score >= 80 ? 'text-emerald-600' :
                              c.score >= 60 ? 'text-amber-600' :
                              'text-red-600'
                            }`}>
                              {c.score.toFixed(0)}
                            </span>
                          )}
                          {c.tier && (
                            <Badge className={`${TIER_COLORS[c.tier] || 'bg-slate-100'} text-xs`}>
                              {c.tier}
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 text-center py-4">No contractor data</p>
                )}
              </CardContent>
            </Card>

            {/* Report Info */}
            <Card className="bg-slate-50 border-slate-200">
              <CardContent className="pt-6">
                <div className="text-center space-y-2">
                  <p className="text-xs text-slate-500">Report Generated</p>
                  <p className="text-sm font-medium text-slate-700">
                    {formatDate(report.generated_at)}
                  </p>
                  <p className="text-xs text-slate-400 mt-4">
                    Report ID: {report.report_id}
                  </p>
                </div>
              </CardContent>
            </Card>

          </div>
        </div>

      </div>

      {/* Print Footer */}
      <div className="hidden print:block mt-8 pt-4 border-t border-slate-200 text-center text-xs text-slate-500">
        <p>Generated by LHS FM Platform • {formatDate(report.generated_at)}</p>
      </div>
    </div>
  );
}

// KPI Card Component
function KPICard({ label, value, icon: Icon, color, alert, isLarge }) {
  const colors = {
    green: 'bg-green-50 text-green-700 border-green-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    red: 'bg-red-50 text-red-700 border-red-200',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
  };

  return (
    <div className={`rounded-2xl border p-5 ${colors[color] || colors.blue} ${alert ? 'ring-2 ring-red-400' : ''}`}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="h-5 w-5 opacity-70" />
        <span className="text-sm font-medium opacity-80">{label}</span>
      </div>
      <div className={`font-bold ${isLarge ? 'text-2xl' : 'text-3xl'}`}>{value}</div>
    </div>
  );
}
